<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import FolderCreateModal from './FolderCreateModal.vue';
import FolderDeleteModal from './FolderDeleteModal.vue';
import draggable from 'vuedraggable';
import AttributeAPI from 'dashboard/api/attributes';

const emit = defineEmits(['open-folder']);
const { t } = useI18n();
const store = useStore();

const folders = computed(() =>
  [...store.getters['kanbanFolders/folders']].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
);
const showModal = ref(false);
const editingFolder = ref(null);
const dragging = ref(false);
const localFolders = ref([]);

const deletingFolder = ref(null);
const showDeleteModal = ref(false);

watch(
  folders,
  val => {
    if (!dragging.value) localFolders.value = [...val];
  },
  { immediate: true }
);

const PRINCIPAL_FOLDER = {
  name: 'Principal',
  icon: 'i-lucide-layers',
  slug: 'principal',
};

const PRINCIPAL_NATIVE_FIELDS_COUNT = 16;

onMounted(async () => {
  await store.dispatch('kanbanFolders/fetchFolders');
  const existing = store.getters['kanbanFolders/folders'];
  if (!existing.find(f => f.slug === 'principal')) {
    try {
      await store.dispatch('kanbanFolders/createFolder', PRINCIPAL_FOLDER);
    } catch {
      // pasta já existe ou erro ignorado
    }
  }
});

function openCreate() {
  editingFolder.value = null;
  showModal.value = true;
}

defineExpose({ openCreate });

function openEdit(folder) {
  editingFolder.value = folder;
  showModal.value = true;
}

function requestDelete(folder) {
  const hasFields = (folder.fields_count ?? 0) > 0;
  deletingFolder.value = { ...folder, _hasFields: hasFields };
  showDeleteModal.value = true;
}

function cancelDelete() {
  deletingFolder.value = null;
  showDeleteModal.value = false;
}

async function confirmDelete({ targetFolderId }) {
  if (!deletingFolder.value) return;
  try {
    if (targetFolderId) {
      const { data: allAttrs } = await AttributeAPI.getAttributesByModel();
      const fieldsToMove = allAttrs.filter(
        a => a.folder_id === deletingFolder.value.id
      );
      await Promise.all(
        fieldsToMove.map(attr =>
          store.dispatch('attributes/update', {
            id: attr.id,
            kanban_folder_id: targetFolderId,
          })
        )
      );
      await store.dispatch('attributes/get');
    }
    await store.dispatch('kanbanFolders/deleteFolder', deletingFolder.value.id);
    useAlert(t('KANBAN.FOLDERS.DELETE_SUCCESS'));
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
  } finally {
    deletingFolder.value = null;
    showDeleteModal.value = false;
  }
}

function isLocked(folder) {
  return folder.slug === 'utm' || folder.slug === 'principal';
}

async function onDragEnd() {
  dragging.value = false;
  try {
    await store.dispatch(
      'kanbanFolders/reorderFolders',
      localFolders.value.filter(f => !isLocked(f)).map(f => f.id)
    );
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
    localFolders.value = [...folders.value];
  }
}

async function handleSubmit({ name, icon, category }) {
  try {
    if (editingFolder.value) {
      await store.dispatch('kanbanFolders/updateFolder', {
        id: editingFolder.value.id,
        name,
        icon,
      });
      useAlert(t('KANBAN.FOLDERS.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('kanbanFolders/createFolder', {
        name,
        icon,
        category,
      });
      useAlert(t('KANBAN.FOLDERS.CREATE_SUCCESS'));
    }
    showModal.value = false;
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
  }
}

const otherFoldersForDelete = computed(() =>
  deletingFolder.value
    ? folders.value.filter(f => f.id !== deletingFolder.value.id)
    : []
);

const principalFolder = computed(
  () => folders.value.find(f => f.slug === 'principal') ?? null
);

const utmFolder = computed(
  () => folders.value.find(f => f.slug === 'utm') ?? null
);

const activeCategory = defineModel('activeCategory', {
  type: String,
  default: 'sales',
});

const draggableFolders = computed({
  get: () =>
    localFolders.value.filter(
      f =>
        f.slug !== 'principal' &&
        f.slug !== 'utm' &&
        (activeCategory.value === 'post_sales'
          ? f.category === 'post_sales'
          : f.category !== 'post_sales')
    ),
  set: val => {
    localFolders.value = val;
  },
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="!localFolders.length"
      class="flex flex-col items-center justify-center py-20 gap-3 text-n-slate-9"
    >
      <span class="i-lucide-folder-open size-10" />
      <p class="text-sm">{{ t('KANBAN.FOLDERS.EMPTY') }}</p>
      <Button sm :label="t('KANBAN.FOLDERS.NEW_FOLDER')" @click="openCreate" />
    </div>

    <div v-else class="flex flex-col">
      <!-- Seletor de categoria — filtra as pastas customizadas abaixo -->
      <div class="flex items-center gap-1 pb-3">
        <div class="flex items-center gap-1 p-1 rounded-lg bg-n-surface-2">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            :class="
              activeCategory === 'sales'
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="activeCategory = 'sales'"
          >
            <span
              class="i-lucide-trending-up size-4"
              :class="activeCategory === 'sales' ? 'text-n-teal-9' : ''"
            />
            {{ t('KANBAN.SETTINGS.CATEGORY_SALES') }}
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            :class="
              activeCategory === 'post_sales'
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="activeCategory = 'post_sales'"
          >
            <span
              class="i-lucide-headset size-4"
              :class="activeCategory === 'post_sales' ? 'text-n-amber-9' : ''"
            />
            {{ t('KANBAN.SETTINGS.CATEGORY_POST_SALES') }}
          </button>
        </div>
      </div>

      <div class="flex flex-col divide-y divide-n-weak border-t border-n-weak">
        <!-- Pasta Principal — sempre fixada no topo, sem editar/excluir/drag -->
        <div
          v-if="principalFolder"
          class="flex items-center gap-2 py-3 px-1 group"
        >
          <span class="w-5 h-8 flex items-center justify-center shrink-0" />
          <button
            class="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
            @click="emit('open-folder', principalFolder)"
          >
            <span
              class="w-8 h-8 flex items-center justify-center rounded-lg bg-n-surface-2 text-n-slate-10"
            >
              <span class="size-4" :class="principalFolder.icon" />
            </span>
            <div class="flex flex-col">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">{{
                  principalFolder.name
                }}</span>
                <span
                  class="text-[10px] leading-none font-medium px-1.5 py-1 rounded shrink-0 text-n-slate-11 bg-n-slate-3"
                >
                  {{ t('KANBAN.SETTINGS.CATEGORY_NATIVE') }}
                </span>
              </div>
              <span class="text-xs text-n-slate-9">
                {{
                  t(
                    'KANBAN.FOLDERS.FIELDS_COUNT',
                    {
                      n:
                        PRINCIPAL_NATIVE_FIELDS_COUNT +
                        (principalFolder.fields_count ?? 0),
                    },
                    PRINCIPAL_NATIVE_FIELDS_COUNT +
                      (principalFolder.fields_count ?? 0)
                  )
                }}
              </span>
            </div>
          </button>
        </div>

        <!-- Pasta UTMs — sempre fixada em segundo, sem editar/excluir/drag -->
        <div v-if="utmFolder" class="flex items-center gap-2 py-3 px-1 group">
          <span class="w-5 h-8 flex items-center justify-center shrink-0" />
          <button
            class="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
            @click="emit('open-folder', utmFolder)"
          >
            <span
              class="w-8 h-8 flex items-center justify-center rounded-lg bg-n-surface-2 text-n-slate-10"
            >
              <span class="size-4" :class="utmFolder.icon" />
            </span>
            <div class="flex flex-col">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">{{
                  utmFolder.name
                }}</span>
                <span
                  class="text-[10px] leading-none font-medium px-1.5 py-1 rounded shrink-0 text-n-slate-11 bg-n-slate-3"
                >
                  {{ t('KANBAN.SETTINGS.CATEGORY_NATIVE') }}
                </span>
              </div>
              <span class="text-xs text-n-slate-9">
                {{
                  t(
                    'KANBAN.FOLDERS.FIELDS_COUNT',
                    { n: utmFolder.fields_count ?? 0 },
                    utmFolder.fields_count ?? 0
                  )
                }}
              </span>
            </div>
          </button>
        </div>

        <!-- Demais pastas arrastáveis (excluindo principal e utm) -->
        <draggable
          v-model="draggableFolders"
          item-key="id"
          handle=".folder-drag-handle"
          ghost-class="opacity-40"
          drag-class="shadow-xl"
          class="flex flex-col divide-y divide-n-weak"
          filter=".no-drag"
          @start="dragging = true"
          @end="onDragEnd"
        >
          <template #item="{ element: folder }">
            <div
              class="flex items-center gap-2 py-3 px-1 group"
              :class="isLocked(folder) ? 'no-drag' : ''"
            >
              <span
                class="w-5 h-8 flex items-center justify-center shrink-0"
                :class="
                  !isLocked(folder)
                    ? 'folder-drag-handle text-n-slate-7 hover:text-n-slate-10 cursor-grab active:cursor-grabbing transition-colors'
                    : ''
                "
              >
                <span
                  v-if="!isLocked(folder)"
                  class="i-lucide-grip-vertical size-4"
                />
              </span>

              <button
                class="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
                @click="emit('open-folder', folder)"
              >
                <span
                  class="w-8 h-8 flex items-center justify-center rounded-lg bg-n-surface-2 text-n-slate-10"
                >
                  <span class="size-4" :class="folder.icon" />
                </span>
                <div class="flex flex-col">
                  <div class="flex items-center gap-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{
                      folder.name
                    }}</span>
                  </div>
                  <span class="text-xs text-n-slate-9">
                    {{
                      t(
                        'KANBAN.FOLDERS.FIELDS_COUNT',
                        { n: folder.fields_count ?? 0 },
                        folder.fields_count ?? 0
                      )
                    }}
                  </span>
                </div>
              </button>

              <div
                v-if="!isLocked(folder)"
                class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <button
                  class="w-8 h-8 flex items-center justify-center rounded text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                  @click="openEdit(folder)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center rounded text-n-ruby-9 hover:bg-n-ruby-2 transition-colors"
                  @click="requestDelete(folder)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <FolderCreateModal
      v-if="showModal"
      :folder="editingFolder"
      @submit="handleSubmit"
      @cancel="showModal = false"
    />

    <FolderDeleteModal
      v-if="showDeleteModal && deletingFolder"
      :folder="deletingFolder"
      :other-folders="otherFoldersForDelete"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>
